import { atom } from "jotai";
import { AccountAppearanceState, SelectedRow } from "../Labeling.types";
import {
    AccountHintType,
    DraftAccount,
} from "@/entities/DataAssociation/Draft.model";
import { isRowDisabled } from "../Labeling.helpers";
import { currentDraftAtom, accountApplicationAtom } from "./draft.state";
import { isReadonlyAtom } from "./params.state";
import { labelingContextViewAtom } from "./context.state";

const baseSelectedRowAtom = atom<SelectedRow | null>(null);

export const selectedRowAtom = atom(
    (get) => {
        const selectedRow = get(baseSelectedRowAtom);
        if (!selectedRow) return null;

        const accountApplication = get(accountApplicationAtom);
        const hasAppliedAccount = !!accountApplication[selectedRow.rowIndex];

        return {
            ...selectedRow,
            hasAppliedAccount,
        };
    },
    (get, set, newValue: SelectedRow | null) => {
        set(baseSelectedRowAtom, newValue);

        if (!newValue) {
            set(labelingContextViewAtom, null);
            return;
        }

        if (newValue?.account?.type === AccountHintType.EXISTING) {
            set(labelingContextViewAtom, {
                type: "ACCOUNT_SELECTION_LIST",
            });
        } else if (newValue?.account?.type === AccountHintType.NEW) {
            set(labelingContextViewAtom, { type: "ACCOUNT_SELECTION_NEW" });
        }
    },
);

export const showReadableLabelAtom = atom<boolean>(true);
export const showLabelHintsAtom = atom<boolean>(true);
export const onRowAccountEditAtom = atom(
    null,
    (
        get,
        set,
        data: {
            colId: string | undefined;
            rowIndex: string;
            account: DraftAccount;
            rowHeader: string;
            isDisabled: boolean;
        },
    ) => {
        const isReadonly = get(isReadonlyAtom);
        console.log("rowClicked", data);
        if (isReadonly || data.isDisabled) return;
        if (
            data.colId !== "accountName" &&
            data.colId !== "label" &&
            data.colId !== "rowHeader"
        )
            return;
        set(selectedRowAtom, {
            rowIndex: data.rowIndex,
            account: data.account,
            rowHeader: data.rowHeader,
        });
    },
);

export const tableRowsAtom = atom((get) => {
    const { data: currentDraft } = get(currentDraftAtom);
    const accountApplication = get(accountApplicationAtom);
    const selectedRow = get(selectedRowAtom);

    if (!currentDraft) return [];

    const { isCodat, isCleaned } = currentDraft;

    const rowData = Object.entries(currentDraft.rows).map(([rowIndex, row]) => {
        let accountAppearanceState = AccountAppearanceState.DEFAULT;
        const isDisabled =
            row.account?.type === AccountHintType.TOTAL ||
            isRowDisabled(isCodat, row.account?.meta?.codatAccountId);
        const userConfirmed = accountApplication[rowIndex];

        if (!userConfirmed) {
            if (isDisabled)
                accountAppearanceState = AccountAppearanceState.DISABLED;
            else if (isCleaned)
                accountAppearanceState = AccountAppearanceState.TENTATIVE;
        }

        return {
            ...row,
            rowIndex,
            isHidden: !userConfirmed && !get(showLabelHintsAtom),
            isDisabled:
                accountAppearanceState === AccountAppearanceState.DISABLED,
            isTentative:
                accountAppearanceState === AccountAppearanceState.TENTATIVE,
            accountAppearanceState,
            isSelected: selectedRow?.rowIndex === rowIndex,
            userConfirmed,
        };
    });

    return rowData;
});
